import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/utils/cn'
import {
  elapsedMs,
  formatCost,
  formatDurationMs,
  formatZonedDateTime,
  type ExecutionRowModel,
  type ExecutionState,
} from './executionState'

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'

export function executionStateVariant(state: ExecutionState): BadgeVariant {
  switch (state) {
    case 'succeeded':
      return 'success'
    case 'failed':
    case 'cancelled':
      return 'destructive'
    case 'awaiting_response':
    case 'awaiting_approval':
      return 'warning'
    case 'running':
      return 'secondary'
    default:
      return 'outline'
  }
}

export function ExecutionStateBadge({ state }: { state: ExecutionState }) {
  const { t } = useTranslation()
  return (
    <Badge variant={executionStateVariant(state)} data-execution-state={state}>
      {t(`work.execution.state.${state}`)}
    </Badge>
  )
}

export interface ExecutionRowProps {
  model: ExecutionRowModel
  selected?: boolean
  onSelect?: () => void
  /** Actions propres à la page (ouvrir, relancer, répondre…). */
  actions?: ReactNode
  testId?: string
  className?: string
}

/**
 * Ligne d'exécution commune à l'Historique, à Activité et aux Exécutions d'un
 * Workflow (os-reorganisation-ecrans.md § 6, OS-04) : état parmi huit, date
 * avec fuseau, durée d'exécution distincte du temps écoulé, coût connu ou
 * « non disponible », résultat ou erreur en une ligne, détail technique replié.
 */
export default function ExecutionRow({ model, selected = false, onSelect, actions, testId, className }: ExecutionRowProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n?.language
  const notAvailable = t('work.execution.notAvailable')
  const started = formatZonedDateTime(model.startedAt, locale)
  const runtime = formatDurationMs(model.runtimeMs)
  const elapsed = formatDurationMs(elapsedMs(model.startedAt, model.endedAt))
  const cost = formatCost(model.cost, locale)
  const outcome = model.outcomeKey ? t(model.outcomeKey) : model.outcome
  const header = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 truncate font-medium">{model.title}</span>
        <ExecutionStateBadge state={model.state} />
      </div>
      <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2 xl:grid-cols-4">
        <div className="min-w-0">
          <dt className="inline">{t('work.execution.started')} : </dt>
          <dd className="inline">{started ?? notAvailable}</dd>
        </div>
        <div>
          <dt className="inline">{t('work.execution.runtime')} : </dt>
          <dd className="inline">{runtime ?? notAvailable}</dd>
        </div>
        <div>
          <dt className="inline">{t('work.execution.elapsed')} : </dt>
          <dd className="inline">{elapsed ?? notAvailable}</dd>
        </div>
        <div>
          <dt className="inline">{t('work.execution.cost')} : </dt>
          <dd className="inline">{cost ?? notAvailable}</dd>
        </div>
      </dl>
      {outcome ? (
        <p
          className={cn(
            'mt-2 truncate text-sm',
            model.outcomeTone === 'error' ? 'text-destructive' : model.outcomeTone === 'warning' ? 'text-warning' : 'text-foreground',
          )}
          title={outcome}
        >
          {outcome}
        </p>
      ) : null}
    </>
  )
  return (
    <div
      data-testid={testId}
      data-execution-state={model.state}
      className={cn(
        'rounded-lg border bg-card p-3 text-sm',
        selected ? 'border-accent bg-accent/10' : 'border-border',
        className,
      )}
    >
      {onSelect ? (
        <button
          type="button"
          aria-pressed={selected}
          onClick={onSelect}
          className="w-full rounded-md text-left"
        >
          {header}
        </button>
      ) : (
        header
      )}
      {actions ? <div className="mt-3 flex flex-wrap items-center gap-2">{actions}</div> : null}
      <details className="mt-2 text-xs text-muted-foreground">
        <summary className="cursor-pointer select-none">{t('work.execution.technical')}</summary>
        <dl className="mt-2 grid gap-1">
          {model.technical.filter((field) => field.value !== model.title).map((field) => (
            <div key={field.label} className="flex min-w-0 gap-2">
              <dt className="shrink-0 font-mono">{field.label}</dt>
              <dd className="min-w-0 break-all font-mono">{field.value}</dd>
            </div>
          ))}
        </dl>
        {model.raw !== undefined ? (
          <details className="mt-2">
            <summary className="cursor-pointer select-none">{t('work.execution.rawRecord')}</summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded-md border border-border bg-muted/30 p-2 font-mono text-[11px]">
              {JSON.stringify(model.raw, null, 2)}
            </pre>
          </details>
        ) : null}
      </details>
    </div>
  )
}
